import { useCallback } from 'react';
import { useEventDraftPatch, } from '@robinpowered/dashboard-apps-common';
import { ComposerMode } from '../types/index';
import { useAddSpaceFromSearch } from './useAddSpaceFromSearch';
/**
 * Subscribe to cross-app draft patches and apply any space changes they
 * carry to the composer's `spaces`:
 *
 * - `addSpaceId` — append-one delta. The office "add to event" flow emits
 *   `emitEventDraftPatch({ eventId, addSpaceId }, 'office')` when the user
 *   picks a space on the map while the composer is parked.
 * - `spaceIds` — full ordered replacement (first is primary). Emitted by
 *   grid surfaces that can switch the draft's spaces (e.g. schedule's
 *   meeting-rooms click routing). Reconciled against the form: spaces
 *   already present are kept (and reordered), missing ones fetched, spaces
 *   absent from the list dropped.
 *
 * Space changes are handled here — directly inside patterns — rather than
 * via the shell's `EventComposerHandle`, because they involve async fetch +
 * transform + de-dupe logic that already lives in `useAddSpaceFromSearch`.
 * The shell still owns the simpler time/date patches it applies through the
 * handle. Note the shell's outgoing echo carries times AND `spaceIds`
 * together, so this hook must tolerate combined patches — the reconcile
 * no-ops on a content-equal list, which also breaks the self-echo loop.
 */
export var useApplyExternalSpacePatch = function (_a) {
    var mode = _a.mode, eventId = _a.eventId;
    var _b = useAddSpaceFromSearch(), addSpaceById = _b.addSpaceById, reconcileSpacesByIds = _b.reconcileSpacesByIds;
    useEventDraftPatch(useCallback(function (patch) {
        // Only apply patches aimed at *this* open.
        if (mode === ComposerMode.CREATE) {
            if (patch.eventId !== null)
                return;
        }
        else if (!eventId || patch.eventId !== eventId) {
            return;
        }
        if (patch.addSpaceId !== undefined) {
            var spaceId = Number(patch.addSpaceId);
            if (Number.isNaN(spaceId))
                return;
            void addSpaceById(spaceId);
            return;
        }
        if (patch.spaceIds !== undefined) {
            void reconcileSpacesByIds(patch.spaceIds);
        }
    }, [mode, eventId, addSpaceById, reconcileSpacesByIds]));
};
